import React, { useEffect, useState, useMemo, useRef } from "react";
import API_BASE from "../config";

// ── LDTManagerModal ────────────────────────────────────────────────────
// Modal view of the LDT library (PRD_followups.md §3/W2 + §3/W2.1):
//   * list every LuminaireLibrary row with Wattage / Lichtstrom / CCT
//   * toggle individual entries enabled/disabled (filter that drives the GA)
//   * bulk Alle aktivieren / Alle deaktivieren operating on the current
//     filtered list — backed by PATCH /luminaires/bulk-enabled so we
//     don't hammer the server with N round trips
//   * extra filters: status (all / enabled / disabled), CCT, series
//   * expandable row that fetches GET /luminaires/{id}/candela and
//     renders C0/C90 polar curves (Lichtstärkeverteilungskurve) on a
//     plain <canvas> — no chart library, ~80 lines of drawing code
//   * banner warning when zero LDTs are enabled, because the GA refuses
//     to run in that state (HTTP 400 from /reoptimize|/submit)
//   * LDT-Upload (Rainer S18, 2026-07-03): "LDTs hochladen" button in the
//     toolbar → hidden <input type="file" multiple accept=".ldt">. Files
//     are read as text in the browser and POSTed as JSON
//     [{filename, content}] to /importldtsfromfolder — same contract the
//     old (unmounted) Topbar used, the endpoint already existed. After a
//     successful upload the list reloads so the new rows appear at once.
//
// Props:
//   isOpen  — bool, when false the component renders nothing
//   onClose — function, called when the user closes the modal
//
// Backend contract:
//   GET   /luminaires                      → list rows
//   PATCH /luminaires/{id}/enabled         → toggle one row
//   PATCH /luminaires/bulk-enabled         → toggle many rows (or all)
//   DELETE /luminaires/{id}                → delete one row
//   GET   /luminaires/{id}/candela         → C0/C90 polar data + max_cd
//   POST  /importldtsfromfolder            → upload [{filename, content}]
// ───────────────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { id: "all", label: "Alle" },
  { id: "enabled", label: "Nur aktiviert" },
  { id: "disabled", label: "Nur deaktiviert" },
];

// Pull a sortable BVP-series prefix (e.g. "BVP518") out of a luminaire
// name/filename. Returns "—" when nothing recognisable is found.
const extractSeries = (lum) => {
  const haystack = (lum.name || "") + " " + (lum.ldtfilename || "");
  const m = haystack.match(/BVP\d{3}/i);
  return m ? m[0].toUpperCase() : "—";
};

// LDT colour entries can come as:
//   "4000K" / "3000K"           — already-formatted Kelvin strings
//   "3000" / "4000" / "5700"    — bare 4-digit Kelvin (new Optivision LDTs)
//   "730" / "740" / "757"       — old Philips lamp codes (3-digit, ×100+1000)
//   array form like [3000]      — postgres array surfaces via String(arr)
// Returns a short label like "3000K" or "—" when nothing recognisable.
const normaliseCct = (c) => {
  if (!c) return "—";
  // Postgres array → take first element if present
  const raw = Array.isArray(c) ? (c[0] ?? "") : c;
  const s = String(raw).trim();
  if (s === "" || s === "—") return "—";
  if (/^\d+K$/i.test(s)) return s.toUpperCase();
  if (/^\d+$/.test(s)) {
    const code = parseInt(s, 10);
    // Full Kelvin value (1000-9999) — Optivision LDTs ship like this.
    // MUST come before the 730/740 branch, otherwise 3000/4000 would
    // wrongly hit the (code >= 7) fallback and render as "300000K".
    if (code >= 1000 && code <= 9999) return `${code}K`;
    // Old Philips 3-digit lamp codes: 730 → 3000K, 740 → 4000K, 757 → 5700K
    if (code >= 700 && code < 800) return `${code % 100 * 100 + 1000}K`;
    // Single/double-digit codes are not standard CCT — return as-is.
    return s + "K";
  }
  return s;
};

const LDTManagerModal = ({ isOpen, onClose }) => {
  const [luminaires, setLuminaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [cctFilter, setCctFilter] = useState("all");
  const [seriesFilter, setSeriesFilter] = useState("all");
  const [sort, setSort] = useState({ column: null, dir: "asc" });
  const [toastMessage, setToastMessage] = useState(null);
  // Expanded row → fetched candela data. Single-row open at a time.
  const [expandedId, setExpandedId] = useState(null);
  const [candelaCache, setCandelaCache] = useState({});       // id → data
  const [candelaLoading, setCandelaLoading] = useState(null); // id during fetch
  // ── LDT-Upload (S18) ──────────────────────────────────────────────
  // uploading disables the button while a POST is in flight; reloadKey
  // is bumped after a successful upload so the list-useEffect (which
  // depends on it) refetches and the new LDTs show up immediately.
  const [uploading, setUploading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const fileInputRef = useRef(null);

  const showToast = (message, duration = 3000) => {
    setToastMessage(message);
    setTimeout(() => setToastMessage(null), duration);
  };

  // ── Initial fetch + refetch on open ──────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/luminaires`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setLuminaires(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load luminaires", err);
        if (!cancelled) showToast(`Fehler beim Laden: ${err.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // reloadKey: bumped by the upload handler → refetch after upload.
  }, [isOpen, reloadKey]);

  // ── Upload handler (S18) ─────────────────────────────────────────────
  // Mirrors the old Topbar handleFolderSelect: read each picked .ldt as
  // plain text and POST the batch as [{filename, content}] JSON to
  // /importldtsfromfolder. The backend parses the photometry and inserts
  // LuminaireLibrary rows (invalid files are skipped server-side).
  const handleUploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    // Reset the input so picking the SAME file twice still fires onChange
    // (browsers suppress the event when the value doesn't change).
    event.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const payload = [];
      for (const file of files) {
        const text = await file.text();
        payload.push({ filename: file.name, content: text });
      }
      const res = await fetch(`${API_BASE}/importldtsfromfolder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // 400 = no valid LDT among the picked files — surface the detail.
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      const n = (result.files_saved || []).length;
      showToast(`${n} LDT-Datei(en) importiert.`);
      setReloadKey((k) => k + 1); // triggers list refetch (see useEffect)
    } catch (err) {
      console.error("LDT upload failed", err);
      showToast(`Upload fehlgeschlagen: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // ── Available CCT / Series filter values are derived from the data ──
  const cctOptions = useMemo(() => {
    const set = new Set(luminaires.map((l) => normaliseCct(l.colour)));
    return ["all", ...Array.from(set).filter((x) => x !== "—").sort()];
  }, [luminaires]);

  const seriesOptions = useMemo(() => {
    const set = new Set(luminaires.map(extractSeries));
    return ["all", ...Array.from(set).filter((x) => x !== "—").sort()];
  }, [luminaires]);

  // ── Filter + sort pipeline (memoised) ───────────────────────────────
  const visibleLuminaires = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    let rows = luminaires;
    if (needle) {
      rows = rows.filter((l) => {
        const name = (l.name || "").toLowerCase();
        const fname = (l.ldtfilename || "").toLowerCase();
        return name.includes(needle) || fname.includes(needle);
      });
    }
    if (statusFilter !== "all") {
      const wantEnabled = statusFilter === "enabled";
      rows = rows.filter((l) => Boolean(l.enabled) === wantEnabled);
    }
    if (cctFilter !== "all") {
      rows = rows.filter((l) => normaliseCct(l.colour) === cctFilter);
    }
    if (seriesFilter !== "all") {
      rows = rows.filter((l) => extractSeries(l) === seriesFilter);
    }
    if (sort.column) {
      const dirFactor = sort.dir === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const av = Number(a[sort.column] ?? 0);
        const bv = Number(b[sort.column] ?? 0);
        return (av - bv) * dirFactor;
      });
    }
    return rows;
  }, [luminaires, filter, statusFilter, cctFilter, seriesFilter, sort]);

  // Aggregates for the warning banner + the "N von M" counter
  const enabledCount = useMemo(
    () => luminaires.filter((l) => l.enabled).length,
    [luminaires]
  );

  // ── Toggles ──────────────────────────────────────────────────────────
  const onHeaderClick = (column) => {
    setSort((prev) => {
      if (prev.column !== column) return { column, dir: "asc" };
      return { column, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };
  const sortMarker = (column) => {
    if (sort.column !== column) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  };

  const toggleEnabled = async (lum) => {
    const next = !lum.enabled;
    try {
      const res = await fetch(`${API_BASE}/luminaires/${lum.id}/enabled`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLuminaires((prev) =>
        prev.map((l) => (l.id === lum.id ? { ...l, enabled: next } : l))
      );
    } catch (err) {
      console.error("Toggle failed", err);
      showToast(`Aktualisierung fehlgeschlagen: ${err.message}`);
    }
  };

  // Bulk toggle operates on the CURRENTLY FILTERED list — that's the
  // intuitive behaviour ("I filtered to show only BVP528, click 'Alle
  // deaktivieren', expect only BVP528 to flip"). When the filter is
  // wide-open the action covers the whole library.
  const bulkToggle = async (nextEnabled) => {
    const ids = visibleLuminaires.map((l) => l.id);
    if (!ids.length) {
      showToast("Keine Einträge in der gefilterten Liste.");
      return;
    }
    const label = nextEnabled ? "aktivieren" : "deaktivieren";
    if (!window.confirm(
      `${ids.length} Leuchte(n) ${label}?` +
      (filter || statusFilter !== "all" || cctFilter !== "all" || seriesFilter !== "all"
        ? " (nur die aktuell gefilterten Einträge)"
        : "")
    )) return;
    try {
      const res = await fetch(`${API_BASE}/luminaires/bulk-enabled`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: nextEnabled, ids }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result = await res.json();
      // Optimistic local update on the affected IDs
      setLuminaires((prev) =>
        prev.map((l) => (ids.includes(l.id) ? { ...l, enabled: nextEnabled } : l))
      );
      showToast(`${result.updated ?? ids.length} Leuchte(n) ${nextEnabled ? "aktiviert" : "deaktiviert"}.`);
    } catch (err) {
      console.error("Bulk toggle failed", err);
      showToast(`Bulk-Aktion fehlgeschlagen: ${err.message}`);
    }
  };

  const deleteLuminaire = async (lum) => {
    const label = lum.ldtfilename || lum.name || `#${lum.id}`;
    if (!window.confirm(`LDT '${label}' wirklich löschen?`)) return;
    try {
      const res = await fetch(`${API_BASE}/luminaires/${lum.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setLuminaires((prev) => prev.filter((l) => l.id !== lum.id));
      if (expandedId === lum.id) setExpandedId(null);
    } catch (err) {
      console.error("Delete failed", err);
      showToast(`Löschen fehlgeschlagen: ${err.message}`);
    }
  };

  // ── Polar diagram fetch (cached per id) ──────────────────────────────
  const expandRow = async (lum) => {
    if (expandedId === lum.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(lum.id);
    if (candelaCache[lum.id]) return;  // already fetched
    setCandelaLoading(lum.id);
    try {
      const res = await fetch(`${API_BASE}/luminaires/${lum.id}/candela`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCandelaCache((prev) => ({ ...prev, [lum.id]: data }));
    } catch (err) {
      console.error("Candela fetch failed", err);
      showToast(`Polardiagramm: ${err.message}`);
    } finally {
      setCandelaLoading(null);
    }
  };

  if (!isOpen) return null;

  // Backdrop click closes; card click stops propagation so it doesn't bubble.
  const handleBackdropClick = () => onClose && onClose();
  const handleCardClick = (e) => e.stopPropagation();

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-card" onClick={handleCardClick}>
        {/* ── Header: title + count + close ──────────────────────── */}
        <div className="modal-header">
          <div>
            <h2>LDT-Bibliothek</h2>
            <span className="modal-meta">
              {visibleLuminaires.length} von {luminaires.length} sichtbar · {enabledCount} aktiviert
            </span>
          </div>
          <button type="button" aria-label="Schließen" className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* ── Warning banner if 0 LDTs are enabled ─────────────────── */}
        {!loading && luminaires.length > 0 && enabledCount === 0 && (
          <div style={{
            margin: "var(--space-3) var(--space-6) 0",
          }}>
            <div className="banner banner-warn">
              <span>⚠</span>
              <span>
                Keine Leuchten aktiviert — die Berechnung lehnt jeden /submit
                oder /reoptimize ab, bis mindestens eine Leuchte aktiviert ist.
              </span>
            </div>
          </div>
        )}

        {/* ── Body: toolbar + table ────────────────────────────────── */}
        <div className="modal-body">
          {/* Toolbar — search input + filter selects + bulk-toggle buttons */}
          <div style={{
            display: "flex", flexWrap: "wrap", alignItems: "center",
            gap: "var(--space-2)", marginBottom: "var(--space-4)",
          }}>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter (Name oder Dateiname)…"
              className="field-input"
              style={{ flex: "1 1 240px", padding: "8px 12px", fontSize: "var(--type-sm)" }}
            />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="field-select"
              style={{ width: "auto", padding: "8px 12px", fontSize: "var(--type-sm)" }}
              title="Aktivierungs-Status filtern"
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s.id} value={s.id}>Status: {s.label}</option>
              ))}
            </select>
            <select
              value={cctFilter}
              onChange={(e) => setCctFilter(e.target.value)}
              className="field-select"
              style={{ width: "auto", padding: "8px 12px", fontSize: "var(--type-sm)" }}
              title="Farbtemperatur filtern"
            >
              {cctOptions.map((c) => (
                <option key={c} value={c}>CCT: {c === "all" ? "Alle" : c}</option>
              ))}
            </select>
            <select
              value={seriesFilter}
              onChange={(e) => setSeriesFilter(e.target.value)}
              className="field-select"
              style={{ width: "auto", padding: "8px 12px", fontSize: "var(--type-sm)" }}
              title="Leuchten-Serie filtern"
            >
              {seriesOptions.map((s) => (
                <option key={s} value={s}>Serie: {s === "all" ? "Alle" : s}</option>
              ))}
            </select>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => bulkToggle(true)}>
              Alle aktivieren
            </button>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => bulkToggle(false)}>
              Alle deaktivieren
            </button>
            {/* ── LDT-Upload (S18): hidden input + proxy button ────────
                multiple erlaubt Datei- UND Mehrfachauswahl; accept ist
                nur ein Picker-Filter, der Server validiert ohnehin. */}
            <input
              ref={fileInputRef}
              type="file"
              accept=".ldt,.LDT"
              multiple
              style={{ display: "none" }}
              onChange={handleUploadFiles}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={uploading}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              title="Eine oder mehrere .ldt-Dateien in die Bibliothek importieren"
            >
              {uploading ? "Lade hoch…" : "⬆ LDTs hochladen"}
            </button>
          </div>

          {loading ? (
            <div className="empty-state">Lade LDT-Bibliothek…</div>
          ) : visibleLuminaires.length === 0 ? (
            <div className="empty-state">
              {luminaires.length === 0
                ? "Noch keine LDTs importiert."
                : "Keine Treffer für die aktuellen Filter."}
            </div>
          ) : (
            <div className="report-card">
              <div className="report-card-body flush">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}></th>
                      <th>Name</th>
                      <th>Datei</th>
                      <th
                        className="sortable num"
                        onClick={() => onHeaderClick("wattage_total")}
                        title="Nach Wattage sortieren"
                      >Wattage (W){sortMarker("wattage_total")}</th>
                      <th
                        className="sortable num"
                        onClick={() => onHeaderClick("flux_total")}
                        title="Nach Lichtstrom sortieren"
                      >Lichtstrom (lm){sortMarker("flux_total")}</th>
                      <th>CCT</th>
                      <th>Aktiv</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLuminaires.map((lum) => {
                      const expanded = expandedId === lum.id;
                      return (
                        <React.Fragment key={lum.id}>
                          <tr>
                            <td>
                              <button
                                type="button"
                                onClick={() => expandRow(lum)}
                                title="Polardiagramm ein-/ausblenden"
                                aria-label="Polardiagramm einblenden"
                                style={{
                                  border: "none", background: "transparent",
                                  color: "var(--text-muted)", cursor: "pointer",
                                  fontSize: "var(--type-sm)", padding: 0,
                                }}
                              >
                                {expanded ? "▾" : "▸"}
                              </button>
                            </td>
                            <td style={{ fontWeight: 600 }}>{lum.name || "—"}</td>
                            <td
                              style={{
                                color: "var(--text-muted)",
                                fontSize: "var(--type-xs)",
                                fontFamily: "var(--font-mono)",
                              }}
                              title={lum.ldtfilename || ""}
                            >
                              {lum.ldtfilename || "—"}
                            </td>
                            <td className="num">{Number(lum.wattage_total ?? 0).toFixed(1)}</td>
                            <td className="num">{Math.round(Number(lum.flux_total ?? 0)).toLocaleString("de-DE")}</td>
                            <td>
                              <span className="pill pill-neutral">
                                {normaliseCct(lum.colour) || "—"}
                              </span>
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                checked={!!lum.enabled}
                                onChange={() => toggleEnabled(lum)}
                                aria-label={`Aktiviert für ${lum.name || lum.id}`}
                                style={{
                                  width: 18, height: 18, cursor: "pointer",
                                  accentColor: "var(--brand-teal-deep)",
                                }}
                              />
                            </td>
                            <td className="actions">
                              <button
                                type="button"
                                className="btn btn-icon"
                                style={{ width: 30, height: 30, fontSize: 12 }}
                                title="Löschen"
                                aria-label={`LDT ${lum.ldtfilename || lum.id} löschen`}
                                onClick={() => deleteLuminaire(lum)}
                              >✕</button>
                            </td>
                          </tr>
                          {expanded && (
                            <tr style={{ background: "var(--bg-soft)" }}>
                              <td colSpan={8} style={{ padding: "var(--space-4) var(--space-5)" }}>
                                <PolarDiagram
                                  data={candelaCache[lum.id]}
                                  loading={candelaLoading === lum.id}
                                />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {toastMessage && <div className="toast">{toastMessage}</div>}
      </div>
    </div>
  );
};

// ── PolarDiagram ───────────────────────────────────────────────────────
// Hand-rolled canvas polar plot showing C0 (blue) + C90 (red) light
// distribution curves for one LDT (Lichtstärkeverteilungskurve, the
// curve Relux/DiaLux show in a luminaire datasheet).
//
// Convention chosen: gamma=0° (nadir, light points straight down) is at
// the TOP of the circle; gamma increases clockwise (g=90 horizontal right,
// g=180 zenith at bottom). Most German lighting datasheets put nadir at
// the top — matches Rainer's expectations.
// ───────────────────────────────────────────────────────────────────────
const PolarDiagram = ({ data, loading }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;
    const ctx = canvas.getContext("2d");
    const w = canvas.width;
    const h = canvas.height;
    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(w, h) / 2 - 22;

    ctx.clearRect(0, 0, w, h);

    // Background reference circles at 25/50/75/100 % of max_cd
    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 1;
    [0.25, 0.5, 0.75, 1.0].forEach((frac) => {
      ctx.beginPath();
      ctx.arc(cx, cy, radius * frac, 0, Math.PI * 2);
      ctx.stroke();
    });
    // Axes
    ctx.beginPath();
    ctx.moveTo(cx, cy - radius); ctx.lineTo(cx, cy + radius);
    ctx.moveTo(cx - radius, cy); ctx.lineTo(cx + radius, cy);
    ctx.stroke();

    // Angle labels (nadir top, horizontal right, zenith bottom, horizontal left)
    ctx.fillStyle = "#6b7280";
    ctx.font = "11px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("0° (Nadir)", cx, cy - radius - 6);
    ctx.fillText("180°", cx, cy + radius + 14);
    ctx.textAlign = "left";
    ctx.fillText("90°", cx + radius + 4, cy + 4);
    ctx.textAlign = "right";
    ctx.fillText("90°", cx - radius - 4, cy + 4);

    const maxCd = data.max_cd > 0 ? data.max_cd : 1;

    const drawCurve = (points, colour, label, labelY) => {
      if (!points || points.length === 0) return;
      // Nadir-at-top convention: screen angle θ (CCW from +x) = -π/2 + g_rad
      //   g = 0°  → θ = -π/2 → straight up (nadir at top)
      //   g = 90° → θ = 0    → right (horizontal)
      //   g = 180°→ θ = π/2  → straight down (zenith)
      ctx.strokeStyle = colour;
      ctx.lineWidth = 2;
      ctx.beginPath();
      points.forEach((p, i) => {
        const r = Math.max(0, Math.min(1, p.cd / maxCd)) * radius;
        const g_rad = (p.g * Math.PI) / 180;
        const screen_a = -Math.PI / 2 + g_rad;
        const x = cx + r * Math.cos(screen_a);
        const y = cy + r * Math.sin(screen_a);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
      // Legend dot + text
      ctx.fillStyle = colour;
      ctx.beginPath();
      ctx.arc(12, labelY, 5, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#374151";
      ctx.textAlign = "left";
      ctx.fillText(label, 22, labelY + 4);
    };

    // C0 (Querachse) uses Signify brand-green-deep so it matches the
    // rest of the UI. C90 (Längsachse) stays the semantic-fail red so
    // the two curves are easy to tell apart at a glance.
    drawCurve(data.c0_plane, "#00b56b", "C0 (Querachse)", h - 28);
    drawCurve(data.c90_plane, "#dc2626", "C90 (Längsachse)", h - 12);

    // max_cd label top-right
    ctx.fillStyle = "#6b7280";
    ctx.textAlign = "right";
    ctx.fillText(
      `max ${Math.round(maxCd).toLocaleString("de-DE")} cd`,
      w - 6, 14
    );
  }, [data]);

  if (loading) {
    return <div style={{ color: "#6b7280", fontSize: 13 }}>Lade Polardiagramm…</div>;
  }
  if (!data) {
    return <div style={{ color: "#6b7280", fontSize: 13 }}>Keine Daten.</div>;
  }
  if ((!data.c0_plane || data.c0_plane.length === 0) &&
      (!data.c90_plane || data.c90_plane.length === 0)) {
    return (
      <div style={{ color: "#6b7280", fontSize: 13 }}>
        Keine Lichtstärkeverteilungs-Daten für diese LDT verfügbar.
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
      <canvas
        ref={canvasRef}
        width={260}
        height={260}
        style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6 }}
      />
      <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>
        <div><strong>Lichtstärkeverteilungskurve</strong></div>
        <div style={{ color: "#6b7280", marginTop: 4 }}>
          Gesamt-Lichtstrom: {Math.round(data.flux_total).toLocaleString("de-DE")} lm<br />
          Gesamt-Leistung: {Number(data.wattage_total).toFixed(1)} W<br />
          Spitzen-Lichtstärke: {Math.round(data.max_cd).toLocaleString("de-DE")} cd
        </div>
        <div style={{ color: "#9ca3af", marginTop: 8, maxWidth: 260, fontSize: 11 }}>
          Nadir (0°) zeigt nach oben. C0 ist die Querachse, C90 die
          Längsachse der Leuchte. Werte direkt aus der LDT-Datei,
          ohne Wartungsfaktor.
        </div>
      </div>
    </div>
  );
};

export default LDTManagerModal;
